/*
 * Colourway swatches.
 *
 * Every colourway is a separate product, so a variant picker cannot show them
 * and there is nothing in the product data linking siblings together. What the
 * products do share is the style name their titles start with. So reduce this
 * product's title to that (see baseTitle) and ask the predictive search
 * endpoint for other products whose titles start with it.
 *
 * The endpoint is asked for a rendered section rather than JSON. The JSON
 * carries ordinary product fields only, so a swatch built from it could never
 * honour the metafields naming which image to use; the section renders the
 * same results through Liquid, where they can be read. So this file no longer
 * builds any markup — it asks for it and puts it in place.
 *
 * Approach ported from the st95 store. Results are cached in sessionStorage,
 * since a shopper clicking between colourways would otherwise repeat the same
 * search on every page.
 */

(function () {
  const CACHE_PREFIX = 'colourSwatches:';
  const CACHE_TTL = 30 * 60 * 1000;
  const SEARCH_LIMIT = 10;
  const SECTION_ID = 'colour-swatch-results';

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const SEPARATOR = '[\\s\\-–—,/|]';

  // Reduce a title to the style name every colourway shares. The colour may be
  // appended, hyphenated, or gone from the title altogether, so "Cap Navy",
  // "Cap - Navy", "Cap -" and "Cap" all have to land on "Cap" — which also
  // holds mid-rename, while some products have been retitled and others have
  // not, since the two still have to find each other.
  function baseTitle(title, colours) {
    const usable = (colours || []).filter(Boolean).map(escapeRegExp);
    let base = String(title || '').trim();

    if (usable.length > 0) {
      base = base.replace(new RegExp(SEPARATOR + '*(' + usable.join('|') + ')$', 'i'), '');
    }

    return base.replace(new RegExp(SEPARATOR + '+$'), '').trim();
  }

  // Null for nothing cached, which a search that found nothing is not.
  function readCache(key) {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > CACHE_TTL) {
        sessionStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return parsed.markup;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, markup) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ timestamp: Date.now(), markup: markup }));
    } catch (error) {
      // Private browsing or a full quota. Not worth failing over.
    }
  }

  async function fetchSwatches(base) {
    const cached = readCache(base);
    if (cached !== null) return cached;

    const params = new URLSearchParams({
      q: base,
      'resources[type]': 'product',
      'resources[limit]': String(SEARCH_LIMIT),
      'resources[options][unavailable_products]': 'last',
      section_id: SECTION_ID,
    });

    const searchUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const response = await fetch(searchUrl + '?' + params.toString());
    if (!response.ok) throw new Error('Search failed: ' + response.status);

    const section = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('#shopify-section-' + SECTION_ID);
    const markup = section ? section.innerHTML.trim() : '';

    writeCache(base, markup);
    return markup;
  }

  function currentPath() {
    return window.location.pathname.replace(/\/$/, '');
  }

  // The section renders the same markup whichever colourway asked for it, so
  // the one being viewed is marked here rather than there.
  function markCurrent(list) {
    list.querySelectorAll('.swatch-link').forEach(function (link) {
      const path = new URL(link.getAttribute('href'), window.location.origin).pathname.replace(/\/$/, '');
      if (path !== currentPath()) return;

      link.classList.add('swatch-link--active');
      link.setAttribute('aria-current', 'page');
    });
  }

  async function render(container) {
    const dataEl = container.querySelector('[data-colour-swatches-product]');
    const list = container.querySelector('[data-colour-swatches-list]');
    if (!dataEl || !list) return;

    let product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    const base = baseTitle(product.title, product.colours);
    // A title that is nothing but the colour gives no prefix to search on.
    if (!base) return;

    let markup;
    try {
      markup = await fetchSwatches(base);
    } catch (error) {
      return;
    }
    if (!markup) return;

    list.innerHTML = markup;

    // One swatch is this product on its own; a lone swatch tells you nothing.
    if (list.querySelectorAll('.swatch-link').length < 2) {
      list.innerHTML = '';
      return;
    }

    markCurrent(list);
  }

  function init() {
    document.querySelectorAll('[data-colour-swatches]').forEach(render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
